import React, { useEffect, useMemo, useState } from 'react';
import { Stack, Button, ButtonProps } from '@mui/material';
import { DataGridPro, DataGridProProps } from '@mui/x-data-grid-pro';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ConfirmModal,
  DrawerComponent,
  ResolutionChip,
  dateColumn,
  tableStyleOverrides,
  textColumn,
} from '@dispatch/web/ui';
import {
  ResultStatus,
  TaskInfo,
  TasksListFilter,
  TaskType,
} from '@dispatch/shared/types';
import { getTimeWithOffset } from '@dispatch/shared';
import { useCompany, useGetTaskInfos } from '@dispatch/shared/hooks';
import { isRunningInJest } from '@dispatch/testing';
import { GeoStatusChip } from './GeoStatusChip';

const EMPTY_DATETIMEOFFSET = '0001-01-01T00:00:00+00:00';
const CANCEL_ROUTE = '/tasks';

// Bulk Lock fetches its own pages via the tasks endpoint and pages at the
// size configured by `bulk_lock_pagination_upper_limit` (default
// 100). The per-page selector is hidden — dispatchers don't pick a size.
// Bulk Cancel uses the legacy "rows from state" model and this constant
// is not applied there — see the route-state branch below.
const BULK_DRAWER_DEFAULT_UPPER_LIMIT = 100;

// Two route-state shapes are supported:
//  - Legacy ("rows" mode): consumed by Bulk Cancel. The parent (TasksList)
//    passes the current List View page of rows; the drawer renders them
//    as-is with no fetching and no server-side pagination.
//  - New ("filters" mode): consumed by Bulk Lock (ADO #98436). The parent
//    passes the filter object; the drawer fetches its own paginated pages
//    via `useGetTaskInfos`.
type DrawerRouteState = {
  height: string;
  width: string;
  pagination?: { pageSize: number; pagingOptions: number[] };
  rowHeight: number;
  rows?: TaskInfo[];
  loading?: boolean;
  filters?: TasksListFilter;
};

type ModalType = 'bulkCancelTask' | 'bulkLockTask';

export type BulkTaskActionDrawerProps = {
  title: string;
  actionButton: {
    label: string;
    color: ButtonProps['color'];
    testId: string;
  };
  modalType: ModalType;
  noRowsMessage: string;
  // Optional client-side row predicate. Used by Bulk Cancel for
  // permission-based gating that can't be expressed server-side. Bulk Lock
  // omits this entirely now that the same exclusions are pushed to the
  // server via `queryFilterOverrides`.
  filterRow?: (row: TaskInfo) => boolean;
  // Filters merged into the server query AFTER the inherited List View
  // filters. Used only in "filters" mode (Bulk Lock). Ignored in legacy
  // "rows" mode (Bulk Cancel).
  queryFilterOverrides?: Partial<TasksListFilter>;
  renderConfirm: (selectedCount: number) => React.ReactElement;
  confirmTitle: string | ((selectedCount: number) => string);
  actionStatus: ResultStatus;
  onConfirm: (selectedRows: TaskInfo[]) => void;
  onModalDeny?: () => void;
};

const buildGridColumns = (): GridColDef[] => [
  textColumn({ field: 'claimNumber', headerName: 'Claim Number', flex: 2 }),
  textColumn({ field: 'insuredName', headerName: 'Insured Name', flex: 2 }),
  textColumn({ field: 'location', headerName: 'Location' }),
  dateColumn({
    field: 'appointment',
    headerName: 'Appointment',
    flex: 1,
    valueGetter: (params) =>
      !!params?.row?.appointment &&
      params?.row?.appointment !== EMPTY_DATETIMEOFFSET &&
      params?.row?.taskType !== TaskType.CLAIM_OWNER
        ? getTimeWithOffset(params?.row?.timeZoneId, params?.row?.appointment)
        : '-',
  }),
  {
    field: 'targetQueue',
    headerName: 'Status',
    renderCell: (params) => {
      const row = params?.row;
      const tooltipString =
        !row.lossLat || !row.lossLong ? 'Address is Incorrect' : '';
      return (
        <Stack direction="row" gap={1}>
          <ResolutionChip
            targetQueue={params.value}
            assignedResourceId={row.assignedResourceId}
            requiredResource={row.requiredResource}
            tooltipString={tooltipString}
          />
          <GeoStatusChip geoStatus={row.geoStatus} showIcon size="small" />
        </Stack>
      );
    },
    flex: 0.5,
    minWidth: 200,
    sortable: false,
  },
];

export const BulkTaskActionDrawer = ({
  title,
  actionButton,
  modalType,
  noRowsMessage,
  filterRow,
  queryFilterOverrides,
  renderConfirm,
  confirmTitle,
  actionStatus,
  onConfirm,
  onModalDeny,
}: BulkTaskActionDrawerProps) => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state: DrawerRouteState };
  const { height, width, pagination, rowHeight, rows, loading, filters } =
    state || {};

  useEffect(() => {
    if (!state) {
      navigate(CANCEL_ROUTE, { replace: true });
    }
  }, [state, navigate]);

  // Use new "filters" mode only when the parent supplied a filter object.
  // Legacy Bulk Cancel always lands in rowsMode (state.rows is set).
  const filtersMode = !!filters;

  // ----- New (filters) mode setup -----
  // Read the tenant-configured page size from CompanyContext. Falls back
  // to 100 when the config is absent or malformed. The dispatcher cannot
  // pick a different page size — page size is set entirely by config.
  const {
    featureFlags: { bulkLockPaginationUpperLimit },
  } = useCompany();
  const upperLimit =
    bulkLockPaginationUpperLimit?.upper_limit &&
    bulkLockPaginationUpperLimit.upper_limit > 0
      ? bulkLockPaginationUpperLimit.upper_limit
      : BULK_DRAWER_DEFAULT_UPPER_LIMIT;

  // Page size is derived (not state) in filters mode so when CompanyContext
  // finishes loading the config after first mount, the new value flows
  // through automatically without the dispatcher having to close and
  // reopen the drawer. Rows mode (Bulk Cancel) keeps its legacy
  // route-state pagination.
  const pageSize = filtersMode ? upperLimit : pagination?.pageSize ?? 10;
  const queryFilters = useMemo<TasksListFilter>(
    () => ({
      ...(filters || {}),
      ...(queryFilterOverrides || {}),
      pageSize,
    }),
    [filters, queryFilterOverrides, pageSize]
  );
  // Hook must be called unconditionally (Rules of Hooks); the third arg
  // disables it in legacy mode so no network call fires.
  const tasks = useGetTaskInfos(queryFilters, null, filtersMode);

  // ----- Mode-agnostic surface -----
  const sourceRows = filtersMode ? tasks.data || [] : rows || [];
  // While the bulk action is running, mark the grid as loading so the
  // dispatcher gets visual feedback that the action is in flight (the
  // confirm modal's button already shows its own loading state, but the
  // grid would otherwise stay interactive behind the modal). The same
  // signal stays on while React Query refetches after the mutation
  // invalidates the `['infos']` cache, so the grid keeps a loading
  // appearance through the transition.
  const isLoading = filtersMode
    ? tasks.isFetching ||
      tasks.status === 'loading' ||
      actionStatus === 'loading'
    : !!loading || actionStatus === 'loading';

  // Client-side `filterRow` runs only when a predicate is passed. Bulk
  // Cancel uses it for permission-based gating (no server equivalent for
  // those checks). Bulk Lock omits it entirely because the same exclusions
  // are pushed to the server via `queryFilterOverrides`.
  const filteredRows = useMemo(
    () => (filterRow ? sourceRows.filter(filterRow) : sourceRows),
    [sourceRows, filterRow]
  );

  // Selection is kept in a Map keyed by task id so picks made on page 1
  // stay selected after the user pages to 2 (and on `onConfirm`, we can
  // return the full TaskInfo for every selected id — not just rows still
  // on screen). The Map approach is used in both modes for consistency.
  const [selectedRowsById, setSelectedRowsById] = useState<
    Map<string, TaskInfo>
  >(() => new Map());
  const [showConfirm, setShowConfirm] = useState(false);

  // When the bulk action settles, close the confirm modal so the user
  // sees the resulting toast. On success we ALSO clear the persisted
  // selection — those rows are now locked and will fall out of the
  // refetched grid anyway. On error we deliberately preserve the
  // selection so a dispatcher who just curated 100 picks can retry
  // without rebuilding the set after a transient failure (network blip,
  // 5xx, validation). Cache invalidation (handled inside the mutation
  // hook) drives the refetch separately.
  const prevActionStatusRef = React.useRef(actionStatus);
  useEffect(() => {
    if (prevActionStatusRef.current === 'loading') {
      if (actionStatus === 'success') {
        setShowConfirm(false);
        setSelectedRowsById(new Map());
      } else if (actionStatus === 'error') {
        setShowConfirm(false);
      }
    }
    prevActionStatusRef.current = actionStatus;
  }, [actionStatus]);

  const selectedTaskIds = useMemo<GridRowId[]>(
    () => Array.from(selectedRowsById.keys()),
    [selectedRowsById]
  );

  const columns = useMemo(() => buildGridColumns(), []);

  const handleSelectionModelChange = (newSelectionModel: GridRowId[]) => {
    const newIds = new Set(newSelectionModel.map(String));
    setSelectedRowsById((prev) => {
      const next = new Map(prev);
      // Remove ids no longer in the selection
      prev.forEach((_value, id) => {
        if (!newIds.has(id)) next.delete(id);
      });
      // Add ids newly selected (must be findable on the current page)
      newIds.forEach((id) => {
        if (!next.has(id)) {
          const row = filteredRows.find((r) => r.id === id);
          if (row) next.set(id, row);
        }
      });
      return next;
    });
  };

  const handleActionClick = () => setShowConfirm(true);
  const handleConfirm = () => onConfirm(Array.from(selectedRowsById.values()));
  const handleDeny = () => {
    setShowConfirm(false);
    onModalDeny?.();
  };

  const resolvedConfirmTitle =
    typeof confirmTitle === 'function'
      ? confirmTitle(selectedTaskIds.length)
      : confirmTitle;

  // Server-paging props only attach in "filters" mode. In rows mode the
  // grid stays in default client-paginated behavior so Bulk Cancel keeps
  // working exactly as before. Page size is config-driven only -- the
  // grid never exposes a per-page selector in filters mode, so no
  // `onPageSizeChange` handler is wired.
  const serverPagingProps = useMemo<Partial<DataGridProProps>>(() => {
    if (!filtersMode) return {};
    const count = tasks.currentTotal || 0;
    return {
      paginationMode: 'server',
      page: tasks.currentPageNumber,
      rowCount: tasks.hasNextPage ? count + 1 : count,
      onPageChange: (newPage: number) => {
        if (newPage > tasks.currentPageNumber) {
          tasks.getNextPage();
        } else if (newPage < tasks.currentPageNumber) {
          tasks.getPreviousPage();
        }
      },
      localeText: {
        MuiTablePagination: {
          labelDisplayedRows: ({ from, to }: { from: number; to: number }) =>
            tasks.hasNextPage
              ? `${from} - ${to} of more than ${to}`
              : `${from} - ${to}`,
        },
      },
    };
  }, [filtersMode, tasks]);

  // In filters mode (Bulk Lock) we hide MUI's rows-per-page selector by
  // passing an empty options list so the dispatcher cannot override the
  // config-driven page size. Rows mode (Bulk Cancel) keeps its legacy
  // selector behavior.
  const rowsPerPageOptions = filtersMode ? [] : pagination?.pagingOptions;

  const actionButtonEl = (
    <Stack px={1}>
      <Button
        data-testid={actionButton.testId}
        type="button"
        variant="outlined"
        color={actionButton.color}
        onClick={handleActionClick}
        disabled={selectedTaskIds.length === 0}
        sx={{
          fontWeight: '700',
          lineHeight: '1.3750em',
          letterSpacing: '0.029em',
        }}
      >
        {actionButton.label}
      </Button>
    </Stack>
  );

  return (
    <div>
      <DrawerComponent
        onClose={() => navigate(CANCEL_ROUTE)}
        renderLeftSideButtons={[actionButtonEl]}
        showSaveButton={false}
        title={title}
        showButtons={true}
        saveDisabled={selectedTaskIds.length === 0}
        sx={{
          width: '65vw',
          '& .MuiBox-root': {
            padding: 0,
          },
        }}
      >
        <div style={{ height, width }}>
          <DataGridPro
            loading={isLoading}
            disableVirtualization={isRunningInJest()}
            onSelectionModelChange={(newSelectionModel) => {
              const selectedIds = Array.isArray(newSelectionModel)
                ? newSelectionModel
                : [newSelectionModel];
              handleSelectionModelChange(selectedIds);
            }}
            rows={filteredRows}
            components={{
              NoRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  {noRowsMessage}
                </Stack>
              ),
              NoResultsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  Filter Returned No Tasks
                </Stack>
              ),
            }}
            selectionModel={selectedTaskIds}
            rowHeight={rowHeight || 32}
            columns={[...columns]}
            pageSize={pageSize}
            rowsPerPageOptions={rowsPerPageOptions}
            pagination
            checkboxSelection
            keepNonExistentRowsSelected
            sx={tableStyleOverrides}
            {...serverPagingProps}
          />
        </div>
      </DrawerComponent>
      {showConfirm && (
        <ConfirmModal
          actionStatus={actionStatus}
          onConfirm={handleConfirm}
          onDeny={handleDeny}
          confirmContent={renderConfirm(selectedTaskIds.length)}
          confirmTitle={resolvedConfirmTitle}
          modalType={modalType}
        />
      )}
    </div>
  );
};
